// Error Tracking & Monitoring System
import { supabase } from './supabase';
import { trackError } from './analytics';

interface ErrorReport {
  message: string;
  stack?: string;
  componentStack?: string;
  url: string;
  userAgent: string;
  timestamp: string;
  userId?: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
  context?: any;
}

// Initialize error tracking
export const initErrorTracking = () => {
  // Global error handler
  window.addEventListener('error', (event) => {
    handleError({
      message: event.message,
      stack: event.error?.stack,
      url: window.location.href,
      userAgent: navigator.userAgent,
      timestamp: new Date().toISOString(),
      severity: 'high',
      context: {
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
      },
    });
  });

  // Unhandled promise rejection handler
  window.addEventListener('unhandledrejection', (event) => {
    handleError({
      message: `Unhandled Promise Rejection: ${event.reason}`,
      stack: event.reason?.stack,
      url: window.location.href,
      userAgent: navigator.userAgent,
      timestamp: new Date().toISOString(),
      severity: 'high',
      context: {
        type: 'unhandledrejection',
        reason: event.reason,
      },
    });
  });

  // Console error override for tracking
  const originalConsoleError = console.error;
  console.error = (...args: any[]) => {
    originalConsoleError.apply(console, args);
    
    // Track console errors
    handleError({
      message: args.map(arg => 
        typeof arg === 'object' ? JSON.stringify(arg) : String(arg)
      ).join(' '),
      url: window.location.href,
      userAgent: navigator.userAgent,
      timestamp: new Date().toISOString(),
      severity: 'medium',
      context: { type: 'console.error', args },
    });
  };
};

// Handle and report errors
const handleError = async (errorReport: ErrorReport) => {
  try {
    // Get current user
    const { data: { user } } = await supabase.auth.getUser();
    errorReport.userId = user?.id;

    // Log to console in development
    if (import.meta.env.DEV) {
      console.error('🚨 Error Tracked:', errorReport);
    }

    // Track to analytics
    trackError(errorReport.message, errorReport.stack, errorReport.context);

    // Store in Supabase for admin review
    await supabase.from('analytics_events').insert({
      event_type: 'error',
      user_id: errorReport.userId,
      metadata: errorReport,
    });

    // Send to external error tracking service (optional)
    // await sendToSentry(errorReport);
    // await sendToRollbar(errorReport);

  } catch (err) {
    // Fail silently to avoid infinite error loops
    console.error('Error tracking failed:', err);
  }
};

// Manual error reporting
export const reportError = (
  error: Error | string,
  severity: ErrorReport['severity'] = 'medium',
  context?: any
) => {
  const errorMessage = typeof error === 'string' ? error : error.message;
  const errorStack = typeof error === 'string' ? undefined : error.stack;

  handleError({
    message: errorMessage,
    stack: errorStack,
    url: window.location.href,
    userAgent: navigator.userAgent,
    timestamp: new Date().toISOString(),
    severity,
    context,
  });
};

// React Error Boundary helper
export class ErrorBoundary extends Error {
  constructor(message: string, public componentStack?: string) {
    super(message);
    this.name = 'ErrorBoundary';
  }
}

export const reportBoundaryError = (
  error: Error,
  errorInfo: { componentStack?: string }
) => {
  handleError({
    message: error.message,
    stack: error.stack,
    componentStack: errorInfo.componentStack,
    url: window.location.href,
    userAgent: navigator.userAgent,
    timestamp: new Date().toISOString(),
    severity: 'critical',
    context: {
      type: 'react_error_boundary',
      errorInfo,
    },
  });
};

// API error tracking
export const trackAPIError = async (
  endpoint: string,
  method: string,
  statusCode: number,
  errorMessage: string,
  requestData?: any
) => {
  await handleError({
    message: `API Error: ${method} ${endpoint} - ${statusCode} - ${errorMessage}`,
    url: window.location.href,
    userAgent: navigator.userAgent,
    timestamp: new Date().toISOString(),
    severity: statusCode >= 500 ? 'high' : 'medium',
    context: {
      type: 'api_error',
      endpoint,
      method,
      statusCode,
      requestData,
    },
  });
};

// Network error tracking
export const trackNetworkError = (url: string, error: any) => {
  handleError({
    message: `Network Error: ${url}`,
    stack: error?.stack,
    url: window.location.href,
    userAgent: navigator.userAgent,
    timestamp: new Date().toISOString(),
    severity: 'medium',
    context: {
      type: 'network_error',
      requestUrl: url,
      error: error?.message,
    },
  });
};

// Performance issue tracking
export const trackPerformanceIssue = (
  metric: string,
  value: number,
  threshold: number
) => {
  if (value > threshold) {
    handleError({
      message: `Performance Issue: ${metric} exceeded threshold (${value}ms > ${threshold}ms)`,
      url: window.location.href,
      userAgent: navigator.userAgent,
      timestamp: new Date().toISOString(),
      severity: 'low',
      context: {
        type: 'performance_issue',
        metric,
        value,
        threshold,
      },
    });
  }
};

export default {
  initErrorTracking,
  reportError,
  reportBoundaryError,
  trackAPIError,
  trackNetworkError,
  trackPerformanceIssue,
};
